/**
 * Service Icon Picker
 * @description 검색 + 큐레이션 목록으로 아이콘 선택 UI 성능 최적화
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Input } from '@shared/ui/primitives';
import { iconMap, availableIconNames, curatedIconNames } from '@/utils/iconMap';

const MAX_SEARCH_RESULTS = 120;

const IconButton = React.memo<{
  iconName: string;
  IconComponent: React.ComponentType<{ className?: string }> | undefined;
  isSelected: boolean;
  onSelect: (iconName: string) => void;
}>(({ iconName, IconComponent, isSelected, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(iconName);
  }, [iconName, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        flex flex-col items-center justify-center p-2 rounded-md border-2 transition-colors
        ${isSelected
          ? 'border-primary bg-primary/10'
          : 'border-border bg-background hover:border-primary/50 hover:bg-accent'
        }
      `}
      title={iconName}
    >
      <span className="sr-only">{iconName}</span>
      {IconComponent ? (
        <IconComponent className={`w-6 h-6 shrink-0 ${isSelected ? 'text-primary' : 'text-foreground'}`} />
      ) : (
        <div className="w-6 h-6 shrink-0 bg-muted rounded" aria-hidden />
      )}
    </button>
  );
});

IconButton.displayName = 'IconButton';

interface ServiceIconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
}

const ServiceIconPickerComponent: React.FC<ServiceIconPickerProps> = ({ value, onChange }) => {
  const [search, setSearch] = useState('');

  const displayedIcons = useMemo(() => {
    const query = search.trim().toLowerCase();
    let names: string[];

    if (!query) {
      names = [...curatedIconNames];
    } else {
      names = availableIconNames
        .filter((name) => name.toLowerCase().includes(query))
        .slice(0, MAX_SEARCH_RESULTS);
    }

    if (value && iconMap[value] && !names.includes(value)) {
      names = [value, ...names];
    }

    return names;
  }, [search, value]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const isSearchLimited =
    search.trim().length > 0 &&
    availableIconNames.filter((name) => name.toLowerCase().includes(search.trim().toLowerCase())).length >
      MAX_SEARCH_RESULTS;

  return (
    <div className="grid gap-2">
      <Input
        value={search}
        onChange={handleSearchChange}
        placeholder="아이콘 검색 (영문·한글 이름)"
        autoComplete="off"
      />
      <p className="text-xs text-muted-foreground">
        {search.trim()
          ? `검색 결과 ${displayedIcons.length}개${isSearchLimited ? ` (최대 ${MAX_SEARCH_RESULTS}개 표시)` : ''}`
          : `자주 쓰는 아이콘 ${displayedIcons.length}개 · 검색하면 전체 lucide 아이콘에서 찾을 수 있습니다`}
      </p>
      <div className="grid grid-cols-6 gap-2 p-4 border rounded-md max-h-64 overflow-y-auto">
        {displayedIcons.map((iconName) => (
          <IconButton
            key={iconName}
            iconName={iconName}
            IconComponent={iconMap[iconName]}
            isSelected={value === iconName}
            onSelect={onChange}
          />
        ))}
      </div>
    </div>
  );
};

export const ServiceIconPicker = React.memo(ServiceIconPickerComponent);

ServiceIconPicker.displayName = 'ServiceIconPicker';
